<div class="sm:col-12 md:col-12 lg:col-12 lg:ml-2 seo-usage-page">
  <div class="surface-card border-1 surface-border border-round-xl overflow-hidden">
    <div class="section-header p-3">
      <h5 class="m-0">SEO Usage</h5>
      <p class="section-description mt-2 mb-0">Filter SEO usage data by date range to review demand and spending behavior.</p>
      <p class="text-600 mt-2 mb-0">{{ organizationName }} (ID: {{ organizationId }}) | Effective range: {{ getRangeLabel() }}</p>
    </div>

    <div class="section-body p-3">
      <form [formGroup]="filterForm" class="p-fluid p-formgrid grid">
        <div class="field col-12 md:col-4">
          <label htmlfor="seoUsageFrom">From</label>
          <p-calendar
            id="seoUsageFrom"
            formControlName="from"
            [showIcon]="true"
            [readonlyInput]="true"
            placeholder="From date"
            dateFormat="yy-mm-dd">
          </p-calendar>
        </div>

        <div class="field col-12 md:col-4">
          <label htmlfor="seoUsageTo">To</label>
          <p-calendar
            id="seoUsageTo"
            formControlName="to"
            [showIcon]="true"
            [readonlyInput]="true"
            placeholder="To date"
            dateFormat="yy-mm-dd">
          </p-calendar>
        </div>

        <div class="field col-12 md:col-4 action-container">
          <gig-action-button
            [buttons]="[
              {
                id: applyActionButtons[0].id,
                label: applyActionButtons[0].label,
                type: applyActionButtons[0].type,
                icon: applyActionButtons[0].icon,
                iconPos: applyActionButtons[0].iconPos,
                color: applyActionButtons[0].color,
                size: applyActionButtons[0].size,
                disabled: isLoading || !canAccessPage
              }
            ]"
            (buttonClick)="onActionButtonClick($event)">
          </gig-action-button>

          <gig-action-button
            [buttons]="[
              {
                id: refreshActionButtons[0].id,
                label: refreshActionButtons[0].label,
                type: refreshActionButtons[0].type,
                icon: refreshActionButtons[0].icon,
                iconPos: refreshActionButtons[0].iconPos,
                color: refreshActionButtons[0].color,
                size: refreshActionButtons[0].size,
                disabled: isLoading || !canAccessPage
              }
            ]"
            (buttonClick)="onActionButtonClick($event)">
          </gig-action-button>
        </div>
      </form>
    </div>
  </div>

  <div *ngIf="validationMessage" class="p-3 mt-3 border-1 surface-border border-round-xl" style="background:#fff5f5;">
    <span class="p-error">{{ validationMessage }}</span>
  </div>

  <div *ngIf="errorMessage" class="p-3 mt-3 border-1 surface-border border-round-xl" style="background:#fff5f5;">
    <span class="p-error">{{ errorMessage }}</span>
  </div>

  <div *ngIf="isLoading && !isLoadedOnce" class="loading-state p-3 mt-3 border-1 surface-border border-round-xl" style="background:#f8fafc;">
    <p-progressSpinner></p-progressSpinner>
  </div>

  <div class="surface-card border-1 surface-border border-round-xl overflow-hidden mt-3" *ngIf="!isForbiddenContext">
    <div class="section-header p-3">
      <h5 class="m-0">Summary</h5>
      <p class="section-description mt-2 mb-0">High-level totals for requests and processed items in the selected range.</p>
    </div>
    <div class="section-body p-3">
      <div class="p-fluid p-formgrid grid summary-grid">
        <div class="field col-12 md:col-6 xl:col-6">
          <div class="metric-banner metric-requests p-3 border-round-xl">
            <div class="metric-label">Total Requests</div>
            <div class="summary-value">{{ summary.requestCount | number:'1.0-0' }}</div>
          </div>
        </div>
        <div class="field col-12 md:col-6 xl:col-6">
          <div class="metric-banner metric-items p-3 border-round-xl">
            <div class="metric-label">Total Items</div>
            <div class="summary-value">{{ summary.totalItemsCount | number:'1.0-0' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="surface-card border-1 surface-border border-round-xl overflow-hidden mt-3" *ngIf="!isForbiddenContext">
    <div class="section-header p-3">
      <h5 class="m-0">Source Reference</h5>
      <p class="section-description mt-2 mb-0">Each source corresponds to a specific feature area in the platform. Use this to understand what is driving your usage.</p>
    </div>
    <div class="section-body p-3">
      <div class="grid source-legend">
        <div class="col-12 md:col-6 xl:col-3">
          <div class="source-legend-card border-1 surface-border border-round-xl p-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-search text-primary"></i>
              <span class="font-semibold text-900">Serp</span>
            </div>
            <p class="text-600 text-sm m-0">Keyword position lookups across Google (organic, local finder, maps) and Bing (organic, local pack). Triggered by the Keyword Search and Competitor Research pages.</p>
          </div>
        </div>
        <div class="col-12 md:col-6 xl:col-3">
          <div class="source-legend-card border-1 surface-border border-round-xl p-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-tag text-primary"></i>
              <span class="font-semibold text-900">Keywords</span>
            </div>
            <p class="text-600 text-sm m-0">Keyword data enrichment — ideas, suggestions, related keywords, historical search volumes, and search intent. Used by keyword planning and research features.</p>
          </div>
        </div>
        <div class="col-12 md:col-6 xl:col-3">
          <div class="source-legend-card border-1 surface-border border-round-xl p-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-file-edit text-primary"></i>
              <span class="font-semibold text-900">ContentAnalysis</span>
            </div>
            <p class="text-600 text-sm m-0">Content analysis lookups used to evaluate and score web content for SEO quality, relevance, and competitive positioning.</p>
          </div>
        </div>
        <div class="col-12 md:col-6 xl:col-3">
          <div class="source-legend-card border-1 surface-border border-round-xl p-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-star text-primary"></i>
              <span class="font-semibold text-900">Reviews</span>
            </div>
            <p class="text-600 text-sm m-0">Review scans across Google, Amazon, TripAdvisor, and TrustPilot. Triggered by the Sentiment &amp; Reviews pages when pulling fresh review data.</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="surface-card border-1 surface-border border-round-xl overflow-hidden mt-3" *ngIf="!isForbiddenContext">
    <div class="section-header p-3">
      <h5 class="m-0">Usage By Source</h5>
      <p class="section-description mt-2 mb-0">Breakdown of SEO usage by source so you can spot what is driving most volume.</p>
    </div>
    <div class="section-body p-0">
      <p-table [value]="summary.sources" [loading]="isLoading" dataKey="source">
        <ng-template pTemplate="header">
          <tr>
            <th>Source</th>
            <th>Requests</th>
            <th>Total Items</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-item>
          <tr>
            <td>{{ item.source }}</td>
            <td>{{ item.requestCount | number:'1.0-0' }}</td>
            <td>{{ item.totalItemsCount | number:'1.0-0' }}</td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="3" class="text-center empty-table-message">
              <div class="empty-message-content">No source usage found for this range.</div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <div class="surface-card border-1 surface-border border-round-xl overflow-hidden mt-3" *ngIf="!isForbiddenContext">
    <div class="section-header p-3">
      <h5 class="m-0">Source Comparison Chart</h5>
      <p class="section-description mt-2 mb-0">Compare requests or total items by source. Select one or many sources to chart together.</p>
    </div>
    <div class="section-body p-3">
      <div class="timeline-controls mb-3">
        <label for="chartMetric" class="mr-2 font-semibold">Metric</label>
        <p-dropdown
          inputId="chartMetric"
          [options]="chartMetricOptions"
          optionLabel="label"
          optionValue="value"
          [ngModel]="selectedChartMetric"
          (ngModelChange)="onChartMetricChanged($event)"
          appendTo="body">
        </p-dropdown>
      </div>

      <div *ngIf="isSourceChartLoading" class="loading-state p-3 mt-2 border-1 surface-border border-round-xl" style="background:#f8fafc;">
        <p-progressSpinner></p-progressSpinner>
      </div>

      <div *ngIf="!isSourceChartLoading && sourceTrendChartData.length > 0; else emptyTimeline" class="chart-wrap">
        <div class="source-chart-frame">
          <gig-position-distribution-chart
            [chartData]="sourceTrendChartData"
            [chartColors]="chartColors"
            [chartPositionNames]="chartPositionNames"
            [dateFormatter]="'dd MMM'"
            chartType="line"
            type="default"
            height="32rem">
          </gig-position-distribution-chart>
        </div>
      </div>

      <ng-template #emptyTimeline>
        <div *ngIf="!isSourceChartLoading" class="text-center text-600 py-6">No source timeline data available for this range.</div>
      </ng-template>
    </div>
  </div>
</div>
